import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";

function UserIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);

  if (!user) return null;

  async function handleSignOut() {
    setOpen(false);
    await signOut();
    await navigate({ to: "/" });
  }

  return (
    <div className="user-menu">
      <button
        ref={triggerRef}
        type="button"
        className="user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="account menu"
        data-testid="nav-user-menu-button"
        onClick={() => setOpen((o) => !o)}
      >
        <UserIcon />
      </button>
      {open && (
        <div ref={menuRef} className="user-menu__panel" role="menu" data-testid="nav-user-menu">
          <div className="user-menu__eyebrow">{user.email}</div>
          <button
            type="button"
            role="menuitem"
            className="user-menu__item"
            data-testid="nav-user-menu-library"
            onClick={() => {
              setOpen(false);
              void navigate({ to: "/library" });
            }}
          >
            library
          </button>
          <button type="button" role="menuitem" className="user-menu__item" disabled>
            profile
          </button>
          <button type="button" role="menuitem" className="user-menu__item" disabled>
            settings
          </button>
          <div className="user-menu__divider" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="user-menu__item user-menu__item--active"
            onClick={() => void handleSignOut()}
            data-testid="nav-user-menu-signout"
          >
            sign out
          </button>
        </div>
      )}
    </div>
  );
}
